import { api } from "@/api";
import axios from "axios";
import { useSavedBlocksStore } from "@/stores/savedBlocksStore";
import { useMjmlStore } from "@/stores/mjmlStore";
import { useSyncedTranslationsStore } from "@/stores/syncedTranslationsStore";
import useBlocksFromAPI from "../useBlocksFromAPI";
import { ISavedBlockApi } from "@/schemas/savedBlock/savedBlockApiSchema";
import { getEmbedderHostname } from "@topol/utils";

function confirmSyncedLangSave(props: {
  pickedLang: string;
  otherLangs: string[];
}): Promise<boolean> {
  return new Promise((resolve) => {
    window.$event.emit("confirmSyncedLangSave", {
      pickedLang: props.pickedLang,
      otherLangs: props.otherLangs,
      resolve,
    });
  });
}

export default () => {
  const savedBlocksStore = useSavedBlocksStore();
  const mjmlStore = useMjmlStore();

  const saveEditingRow = async () => {
    const sectionIndex = mjmlStore.getSectionIndexByUid(
      savedBlocksStore.editingRowUid
    );

    const editingRow = mjmlStore.getSelectedSectionByIndex(sectionIndex);

    if (editingRow && api.SAVED_SECTIONS) {
      const entry = useSyncedTranslationsStore().getSectionTranslation(
        editingRow.uid
      );
      // Confirm only when the template CURRENTLY has no language mutations, so a
      // template that gained languages after load doesn't show a stale dialog.
      const templateHasNoLangs =
        !mjmlStore.mjml.langs || mjmlStore.mjml.langs.length === 0;
      if (templateHasNoLangs && entry?.pickedLang) {
        const allLangs = Object.keys(entry.fullTranslation || {});
        if (allLangs.length > 1) {
          const confirmed = await confirmSyncedLangSave({
            pickedLang: entry.pickedLang,
            otherLangs: allLangs.filter((l) => l !== entry.pickedLang),
          });
          if (!confirmed) return;
        }
      }

      const translation = mjmlStore.getSectionTranslationByIndex(sectionIndex);
      const { editSavedBlock } = useBlocksFromAPI();
      const isUpdated = await editSavedBlock(editingRow.syncedId, {
        definition: editingRow,
        translation,
      });
      mjmlStore.selectSection({
        sectionIndex: sectionIndex,
      });
      if (isUpdated) savedBlocksStore.editingRowUid = undefined;
    }
  };

  const updateSyncedSections = async () => {
    if (!api.SAVED_SECTIONS) return;
    window.$event.emit("refreshSyncedSectionsList");
    const mjmlSections = mjmlStore.getMjml().children[0].children;
    const { getSavedSectionMjml } = useBlocksFromAPI();
    for (const section of mjmlSections) {
      if (section.syncedId !== undefined) {
        const syncedSection = await getSavedSectionMjml({
          sync: true,
          id: section.syncedId,
          oldUid: section.uid,
          syncedLang: section.syncedLang,
        });
        if (syncedSection) {
          if (mjmlStore.mjml.langs) {
            for (const column of section.children) {
              for (const block of column.children) {
                mjmlStore.deleteBlockTranslation(block);
              }
            }
          }
          mjmlSections.splice(mjmlSections.indexOf(section), 1, syncedSection);
        } else if (syncedSection === false) {
          delete section.syncedId;
          delete section.syncedLang;
        }
      }
    }
  };

  function getParams() {
    return {
      key: mjmlStore.authorize.apiKey,
      hostname: getEmbedderHostname(),
      entity_id: mjmlStore.authorize.userId,
    };
  }

  async function getSyncedSection(id: number) {
    const params = getParams();

    if (api.SAVED_SECTIONS) {
      const data: ISavedBlockApi = await axios
        .get(api.SAVED_SECTIONS + "/" + id, { params: params })
        .then((res) => {
          if (
            !res.data.success ||
            !res.data.data ||
            res.data.data.type !== "synced_section"
          ) {
            return null;
          }
          return res.data.data;
        });

      return data;
    }
  }

  return {
    saveEditingRow,
    updateSyncedSections,
    getSyncedSection,
  };
};
